import Vue from 'vue'
import { mapState } from 'vuex'
import VueTheMask from 'vue-the-mask'
import { FRONTEND_ERROR_CODES, DEFAULT_TIMEZONE } from '~/utils/constants'

Vue.use(VueTheMask)
// Make sure to pick a unique name for the flag
// so it won't conflict with any other mixin.
if (!Vue.__global__) {
  Vue.__global__ = true
  Vue.mixin({
    data() {
      return {
        newCode: null,
        FRONTEND_ERROR_CODES,
        DEFAULT_TIMEZONE
      }
    },
    computed: {
      ...mapState({
        languages: state => state.master.languages,
        userPermissions: state => state.settings.userPermissions,
        currentPermissionCode: state => state.settings.currentPermissionCode,
        systemLocale: state => state.settings.locale,
        systemLocales: state => state.master.languages,
        cmsLanguages: state => state.cmses.selectedCurrencyLanguages,
        translationString: state => state.master.translationString,
        globalDefaultCurrency: state => state.settings.globalSelectedCurrency,
        permissions: state => state.settings.userPermissions,
        errorStrings: state => state['website-setting']['error-code'].errorStrings
      }),
      isStaging() {
        return this.$config.LANDING_DOMAIN === 'cslvault.com'
      },
      isVipProgramActive() {
        return this.$store.state.settings.isVipProgramActive
      }
    },
    mounted() {
      // Add try catch, since first reload this.$cookie is undefined
      try {
        if (this.$cookie?.get('locale')) {
          this.$store.dispatch('settings/locale', this.$cookie?.get('locale'))
        }
        if (!this.$cookie?.get('permissionCode')) {
          this.$cookie.set('permissionCode', 'dashboard')
        }
      } catch (error) {}
    },
    methods: {
      // filter VIP Tier
      filterVIPTierItem(item) {
        if (!item?.vip_tier_id) return null
        try {
          const tier = this.vipTierLists?.find(t => t.id === item?.vip_tier_id)

          const tierName = tier
            ? tier.name?.[this.systemLocale] ||
              tier.name?.['en-US'] ||
              tier.name?.[Object.keys(tier.name)[0]] ||
              ''
            : ''

          return {
            previewImage: tier
              ? `${this.$config.BUCKET}/assets/vip_system/tiers/${tier.file}`
              : null,
            tierName
          }
        } catch (error) {}
      },
      // Check URL
      isURL(string) {
        let url
        try {
          url = new URL(string)
        } catch (_) {
          // If the URL creation fails, check if it starts with 'www.'
          if (string && string.startsWith('www.') && string.split('.')?.length >= 3) {
            // Prepend 'https://' to the string and try again
            try {
              url = new URL('https://' + string)
            } catch (innerError) {
              return false
            }
          } else {
            return false
          }
        }

        const hostname = url.hostname
        const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        // Check if the protocol is 'http://' or 'https://' and if the domain is valid
        return (
          (url.protocol === 'https:' || url.protocol === 'http:') && domainPattern.test(hostname)
        )
      },
      displayFlag(code) {
        return `fi fi-${code?.split('-')[1]?.toLowerCase()}`
      },
      displayLanguageName(code) {
        const iso = code.split('-')[0]
        const languageNames = new Intl.DisplayNames(['en'], {
          type: 'language'
        })
        return languageNames.of(iso)
      },
      copyToClipboard(text) {
        try {
          if (!text) {
            this.$toast.error(this.tl('noContentCopied'))
            return
          }

          if (!navigator?.clipboard) {
            throw new Error('Browser is not included build-in copy function')
          }

          navigator.clipboard.writeText(text)
          // notify user
          this.$toast.show(this.tl('contentCopied'))
        } catch (error) {
          this.$toast.error(this.tl('browserIsNotSupportToCopy'))
        }
      },
      tl(code) {
        if (typeof code === 'number') {
          return this.tr(code)
        }
        if (Object.keys(this.translationString).length === 0) {
          return code
        }

        if (!this.translationString[code]) {
          const text = {}
          const form = {}
          for (let index = 0; index < this.systemLocales.length; index++) {
            text[this.systemLocales[index].code] = code
          }
          form.text = { ...text }
          form.code = code
          form.status = true
          form.used_in = [2]

          //  ? Uncommented this line if you wanna to add dictionary automatically, [dev mode only]

          // if (!this.$store.state['language-strings'].newCodes.includes(code) && this.$cookie.get('token')) {
          // 	this.$store.dispatch(
          // 		'language-strings/addLanguageStrings',
          // 		form
          // 	)
          // }
          // this.$store.dispatch('language-strings/newCode', code);
          return code
        }

        return this.translationString[code]
      },
      tr(errorCode) {
        if (!errorCode) {
          throw new Error('Error code is required')
        }
        return this.errorStrings[errorCode] || errorCode?.toString()
      },
      formatCurrencyJS(value, removeDecimal = false) {
        if (!value) value = 0
        const response = this.$currencyJs(parseFloat(value), {
          separator: ',',
          symbol: ''
        }).format()

        return response.replace('.00', '')
      },
      formatCoinAmount(value) {
        // Always remove decimals
        return this.formatCurrencyJS(parseInt(value), true)
      },
      unFormatCurrency(amount) {
        return this.$currencyJs(amount).value
      },
      readableNumber(number) {
        if (number) {
          return new Intl.NumberFormat().format(parseFloat(number))
        }
        return 0
      },
      // Generate the excel.
      generateExcel(fileName, excelData) {
        let columnNames = ''
        const csvString = excelData.map(item => {
          const tempColumnNames = []
          const columnValues = []
          for (const [key, value] of Object.entries(item)) {
            columnValues.push(value)
            tempColumnNames.push(key)
          }
          columnNames = tempColumnNames
          return columnValues
        })

        columnNames = columnNames.join()
        csvString.forEach(function (row) {
          columnNames += '\n' + row.join(',')
        })

        const hiddenElement = document.createElement('a')
        hiddenElement.href =
          'data:text/csv;charset=utf-8,' + encodeURIComponent('\uFEFF' + columnNames)
        hiddenElement.target = '_blank'
        hiddenElement.download = fileName + '.csv'
        hiddenElement.click()
      },
      generateCSV(fileName, colNames, dataObject) {
        let columnNames = ''
        const csvString = dataObject.map(item => {
          const tempColumnNames = []
          const columnValues = []
          for (const [key, value] of Object.entries(item)) {
            columnValues.push(value)
            tempColumnNames.push(key)
          }
          columnNames = colNames && colNames?.length > 0 ? colNames : tempColumnNames
          return columnValues
        })

        columnNames = columnNames.join()
        csvString.forEach(function (row) {
          columnNames += '\n' + row.join(',')
        })

        const hiddenElement = document.createElement('a')
        hiddenElement.href =
          'data:text/csv;charset=utf-8,' + encodeURIComponent('\uFEFF' + columnNames)
        hiddenElement.target = '_blank'
        hiddenElement.download = fileName + '.csv'
        hiddenElement.click()
      },
      dayJS(date, showTime = true) {
        if (!date) return '-'
        if (showTime) {
          // Check if it's an ISO 8601 date
          if (this.$dayjs(date).isValid() && date?.toString()?.includes('T')) {
            return this.$dayjs(date).format('DD-MM-YYYY HH:mm:ss') // Convert to Bangkok timezone
          } // Define possible input formats
          const formats = [
            'DD/MM/YYYY HH:mm:ss',
            'DD/MM/YYYY HH:mm',
            'YYYY/MM/DD HH:mm:ss',
            'YYYY/MM/DD HH:mm',
            'DD-MM-YYYY HH:mm:ss',
            'DD-MM-YYYY HH:mm',
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD HH:mm'
          ]

          // Try parsing the date
          const parsedDate = this.$dayjs(date, formats, true)

          // Validate if the parsed date is correct
          if (!parsedDate?.isValid()) {
            return 'Invalid Date'
          }

          // Determine the return format based on the original input
          if (date?.includes('/')) {
            if (date.length === 16) {
              return parsedDate.format('DD-MM-YYYY HH:mm')
            } else {
              return parsedDate.format('DD-MM-YYYY HH:mm:ss')
            }
          } else {
            return parsedDate.format(
              date.length === 16 ? 'DD-MM-YYYY HH:mm' : 'DD-MM-YYYY HH:mm:ss'
            )
          }
        } else {
          return this.$dayjs(date).format(`DD-MM-YYYY`)
        }
      },
      getNameFromLocale(langObj) {
        // Return a default value if the language object is not provided or empty
        if (!langObj || Object.keys(langObj).length === 0) {
          return this.tl('noDataAvailable')
        }

        // If only one language exists in the langObj object, return its value
        if (Object.keys(langObj).length === 1) return langObj[Object.keys(langObj)[0]]

        //  If there are multiple languages, return its with locale or en-US
        return langObj[this.systemLocale] || langObj['en-US'] || '-'
      },
      typingSpecialNumbers(event) {
        const notAllowDash = /^[0-9.,-]/g
        if (
          !notAllowDash.test(event.key) &&
          event.key !== 'Backspace' &&
          event.key !== 'Tab' &&
          event.key !== 'ArrowLeft' &&
          event.key !== 'ArrowRight'
        ) {
          event.preventDefault()
        }
      },
      rulesEmptyRequired(text = this.tr(this.FRONTEND_ERROR_CODES.isRequired)) {
        return [v => !!v || text]
      },
      isSuperAdmin() {
        return ~~this.$cookie.get('user')?.user_type === 1
      },
      disabledPlayerProfilePage() {
        if (this.isSuperAdmin()) return false
        const playerPermission = this.permissions.find(el => el.code === 'player_list')
        if (!playerPermission) return false
        if (!playerPermission.can_view) return true
        return false
      },
      goToPlayerProfile(playerId) {
        if (this.disabledPlayerProfilePage()) return null
        window.open(`/players/players/${playerId}`)
      },
      playerAge(createdAt) {
        return `${this.$dayjs(this.$dayjs().format('YYYY-MM-DD')).diff(
          this.$dayjs(createdAt).format('YYYY-MM-DD'),
          'day'
        )} ${this.tl('days')}`
      },
      textInputRegExgKeydown(event, validKeyPattern = null) {
        // Valid characters for numbers and special symbols
        if (!validKeyPattern) return
        // Allow common control/navigation keys, including most standard keys
        const isControlKey =
          event.key === 'Backspace' ||
          event.key === 'Tab' ||
          event.key === 'ArrowLeft' ||
          event.key === 'ArrowRight' ||
          event.key === 'Enter' ||
          event.key === 'Delete' ||
          event.key === 'Home' ||
          event.key === 'End' ||
          event.key === 'Escape' ||
          event.ctrlKey ||
          event.metaKey ||
          event.altKey

        // If it's not a valid number key or a control/navigation key, prevent the input
        if (!(isControlKey || validKeyPattern.test(event.key))) {
          event.preventDefault()
        }
      },
      getCoinImg(setting) {
        // If the API response is not available, return nothing
        if (!setting?.mission_type_settings) return ''
        if (setting?.file)
          return `${this.$config.BUCKET}/assets/mission_control/basic_settings/${setting?.file}`
        return `/coin.png`
      },

      formatMatchTime(dateTime, option = '') {
        const time = this.$dayjs.tz(dateTime, 'America/New_York')
        const bangkokTime = time.tz('Asia/Bangkok')
        if (option === 'value') {
          return bangkokTime.valueOf()
        }
        return bangkokTime.format('DD/MM HH:mm')
      }
    }
  }) // Set up your mixin then
}
